import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useLazyQuery } from "@apollo/client";
import { Popper, Box, Stack, IconButton, Divider, Paper, List, ListItem, ListItemAvatar, ListItemText, Avatar } from '@mui/material';
import { useEditor, EditorContent, ReactRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Mention from '@tiptap/extension-mention';
import EmojiPicker from 'emoji-picker-react';
import { computePosition, flip, shift } from '@floating-ui/dom';

import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';

import messagingContactsLookup from "../../../graphql/queries/messagingContactsLookup";

// ---------- MentionList React component ----------
const MentionList = ({ items, command }) => {
  return (
    <Paper elevation={3}>
      <List>
        {items.map(item => (
          <ListItem button key={item.id} onClick={() => command(item)}>
            <ListItemAvatar>
              <Avatar src={item.avatar} />
            </ListItemAvatar>
            <ListItemText primary={item.label} />
          </ListItem>
        ))}
      </List>
    </Paper>
  );
};

// ---------- Utility to position the dropdown ----------
const updatePosition = (editor, element, clientRect) => {
  if (!clientRect) return;

  computePosition({ getBoundingClientRect: clientRect }, element, {
    placement: 'bottom-start',
    strategy: 'absolute',
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y + window.scrollY}px`;
  });
};

// ---------- TipTapMentionEditor ----------
const TipTapMentionEditor = forwardRef(({
  value,
  onChange,
  onBlur,
  onEnterPressed,
  placeholder = '',
  editable = true,
  minHeight = 150,  
  disableMentions = false,
  disableToolbar = false,
}, ref) => {
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const emojiButtonRef = useRef(null);

  const [getContacts] = useLazyQuery(messagingContactsLookup);

  const extensions = [
    StarterKit.configure({
      keyboardShortcuts: false, // we override Enter behavior manually
    }),
    Placeholder.configure({ placeholder, emptyNodeClass: 'is-empty' }),
  ];

  if (!disableMentions) {
    extensions.push(
      Mention.configure({
        HTMLAttributes: { class: 'mention' },
        suggestion: {
          char: '@',
          startOfLine: false,
          items: async ({ query }) => {
            if (!query || query.length < 3) return [];
            const { data } = await getContacts({ variables: { searchTerm: query } });
            if (!data) return [];
            return (data.directMessages.recipientLookup || []).map(user => ({
              id: user.userId,
              label: `${user.firstname} ${user.lastname}`,
              avatar: user.avatarUrl,
            }));
          },
          render: () => {
            let component;
            return {
              onStart: props => {
                component = new ReactRenderer(MentionList, {
                  props,
                  editor: props.editor,
                });
                component.element.style.zIndex = 20000;
                document.body.appendChild(component.element);
                updatePosition(props.editor, component.element, props.clientRect);
              },
              onUpdate: props => {
                component.updateProps(props);
                updatePosition(props.editor, component.element, props.clientRect);
              },
              onKeyDown: props => {
                if (props.event.key === 'Escape') {
                  component.destroy();
                  return true;
                }
                return component.ref?.onKeyDown?.(props);
              },
              onExit: () => {
                component.element.remove();
                component.destroy();
              },
            };
          },
        },
      })
    );
  }

  const editor = useEditor({
    extensions,
    content: value || "",
    editable,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    onBlur: ({ editor }) => onBlur?.(editor.getHTML()),
    editorProps: {
      handleKeyDown(view, event) {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          onEnterPressed?.(editor.getHTML());
          return true;
        }
        return false;
      }
    }
  });

  useEffect(() => {
    if (editor && value !== undefined && value !== editor.getHTML()) {
      editor.commands.setContent(value, false);
    }
  }, [value, editor]);

  useImperativeHandle(ref, () => ({
    focus: () => editor?.commands.focus(),
    clear: () => editor?.commands.setContent(''),
    getHTML: () => editor?.getHTML(),
    insertEmoticon: emoji => editor?.chain().focus().insertContent(emoji).run(),
  }));

  const renderButton = (icon, onClick, active, buttonRef = null) => (
    <IconButton
      size="small"
      sx={{ '& svg': { fontSize: '16px' } }}
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      color={active ? 'primary' : 'default'}
      ref={buttonRef}
    >
      {icon}
    </IconButton>
  );

  if (!editor) return null;

  return (
    <Box>
      {/* Toolbar */}
      {!disableToolbar && (
        <>
          <Stack direction="row" spacing={1}>
            {renderButton(<FormatBoldIcon />, () => editor.chain().focus().toggleBold().run(), editor.isActive('bold'))}
            {renderButton(<FormatItalicIcon />, () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'))}
            {renderButton(<FormatListBulletedIcon />, () => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'))}
            {renderButton(<FormatListNumberedIcon />, () => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'))}
            {renderButton(<FormatQuoteIcon />, () => editor.chain().focus().toggleBlockquote().run(), editor.isActive('blockquote'))}
            {renderButton(
              <InsertEmoticonIcon />,
              () => setEmojiAnchor(emojiAnchor ? null : emojiButtonRef.current),
              false,
              emojiButtonRef
            )}
          </Stack>
          <Divider sx={{ mt: 1 }} />
        </>
      )}

      {/* Editor */}
      <Box
        sx={{
          border: '1px solid #ccc',
          borderRadius: 1,
          minHeight,
          '& .ProseMirror': {
            outline: 'none',
            padding: 1,
            minHeight,
            cursor: editable ? 'text' : 'default',
            position: 'relative',
            '& p:first-of-type': { marginTop: 0 },
            '& p.is-empty:first-child::before': {
              content: 'attr(data-placeholder)',
              color: '#888',
              fontStyle: 'italic',
              position: 'absolute',
              left: 8,
              top: 8,
              pointerEvents: 'none',
              whiteSpace: 'pre-wrap',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* Emoji picker */}
      {!disableToolbar && (
        <Popper open={Boolean(emojiAnchor)} anchorEl={emojiAnchor} placement="bottom-start" style={{ zIndex: 2000 }}>
          <EmojiPicker
            onEmojiClick={emojiObject => {
              editor.chain().focus().insertContent(emojiObject.emoji).run();
              setEmojiAnchor(null);
            }}
            searchDisabled
            previewConfig={{ showPreview: false }}
            emojiStyle="native"
            emojiVersion="4.0"
            height={300}
            width={300}
          />
        </Popper>
      )}
    </Box>
  );
});

export default TipTapMentionEditor;